import { AutocompleteWithLabel, InputWithLabel } from '@adotel/components'
import { useQuery } from '@apollo/client'
import { ButtonBase } from '@material-ui/core'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import DeleteIcon from '@material-ui/icons/Delete'
import React, { useEffect, useState } from 'react'

import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { getTranslate, lang } from '../../localization'
import { Accommodation } from '../../types/accommodation'
import { BookingRedirect } from '../../types/sitekey'
import {
  rawSiteKeyCode,
  siteKeyAccommodationOptions
} from './siteKeyAccommodationOptions'
import useStyles from './styles/SitekeyBookingRedirect.styles'

type SiteKeyBookingRedirectProps = {
  bookingRedirectsState: BookingRedirect[]
  index: number
  setBookingRedirectsState: React.Dispatch<
    React.SetStateAction<BookingRedirect[]>
  >
  isDefault: boolean
}

export const SiteKeyBookingRedirect: React.FC<SiteKeyBookingRedirectProps> = ({
  bookingRedirectsState,
  index,
  setBookingRedirectsState,
  isDefault
}) => {
  const classes = useStyles()

  const [
    autoCompleteLabelForAccommodation,
    setAutoCompleteLabelForAccommodation
  ] = useState<any>({
    value: bookingRedirectsState[index].Code,
    label: bookingRedirectsState[index].Name
  })

  const { data: accommodations, loading: loadingAccs } = useQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  // A row saved before the raw-code fix holds an acc_ token; store its raw code instead.
  useEffect(() => {
    const code = bookingRedirectsState[index].Code
    const raw = rawSiteKeyCode(code, accommodations?.accommodations)
    if (raw !== code) {
      setBookingRedirectsState((preState) => [
        ...preState.slice(0, index),
        { ...preState[index], Code: raw },
        ...preState.slice(index + 1)
      ])
      setAutoCompleteLabelForAccommodation((label: any) => ({
        ...label,
        value: raw
      }))
    }
  }, [accommodations?.accommodations])

  useEffect(() => {
    if (isDefault) {
      setBookingRedirectsState((preState) => [
        ...preState.slice(0, index),
        {
          BookingRedirect: preState[index].BookingRedirect,
          Code: 'Default',
          Name: 'Default'
        },
        ...preState.slice(index + 1)
      ])
    }
  }, [isDefault])

  return (
    <div className={classes.root}>
      <AutocompleteWithLabel
        label={getTranslate(isDefault ? 'Default' : 'Accommodation')}
        placeholder={getTranslate('Please Select Accommodation')}
        value={autoCompleteLabelForAccommodation}
        getOptionSelected={(option, value) =>
          option.value === value.value && option.label === value.label
        }
        onChange={(_, value: any) => {
          if (value) {
            setAutoCompleteLabelForAccommodation({
              value: value.value,
              label: value.label
            })

            setBookingRedirectsState((preState) => [
              ...preState.slice(0, index),
              {
                BookingRedirect: preState[index].BookingRedirect,
                Code: value.value,
                Name: value.label
              },
              ...preState.slice(index + 1)
            ])
          } else {
            setAutoCompleteLabelForAccommodation(undefined)

            setBookingRedirectsState((preState) => [
              ...preState.slice(0, index),
              {
                BookingRedirect: preState[index].BookingRedirect,
                Code: '',
                Name: ''
              },
              ...preState.slice(index + 1)
            ])
          }
        }}
        options={siteKeyAccommodationOptions(
          accommodations?.accommodations,
          getTranslate('Default'),
          lang
        )}
        disabled={isDefault}
        size="small"
        inputSize="small"
        loading={loadingAccs}
        loadingText={getTranslate('Loading') + '...'}
      />
      <InputWithLabel
        label={getTranslate('Booking Redirect')}
        autoComplete="off"
        inputSize="small"
        value={bookingRedirectsState[index].BookingRedirect}
        onChange={(e) => {
          setBookingRedirectsState((preState) => [
            ...preState.slice(0, index),
            {
              BookingRedirect: e.currentTarget.value,
              Code: preState[index].Code,
              Name: preState[index].Name
            },
            ...preState.slice(index + 1)
          ])
        }}
      />
      <div className={classes.buttonsContainer}>
        <ButtonBase
          className={[
            classes.greenColor,
            bookingRedirectsState.length - 1 > index && classes.greyColor
          ].join(' ')}
          onClick={() => {
            setBookingRedirectsState((preState) => {
              return [
                ...preState,
                {
                  Name: '',
                  BookingRedirect: '',
                  Code: ''
                }
              ]
            })
          }}>
          <AddCircleOutlineIcon />
        </ButtonBase>
        <ButtonBase
          className={classes.redColor}
          onClick={() => {
            setBookingRedirectsState((preState) => [
              ...preState.slice(0, index),
              ...preState.slice(index + 1)
            ])
          }}>
          <DeleteIcon />
        </ButtonBase>
      </div>
    </div>
  )
}
